<template>
  <BaseModal
    :is-open="isOpen"
    title="Fill Seats with Bots"
    max-width="550px"
    @close="handleCancel"
  >
    <div class="fill-bots-container">
      <!-- Info Icon -->
      <div class="info-icon">
        <span class="material-symbols-outlined">smart_toy</span>
      </div>

      <!-- Main Message -->
      <div class="main-message">
        <h3 class="message-title">Fill remaining seats with AI players?</h3>
        <p class="message-text">
          This will add bots to all empty seats so you can start the game immediately.
        </p>
      </div>

      <!-- Empty Seats Info -->
      <div class="seats-info">
        <div class="info-item">
          <span class="info-label">Empty Seats:</span>
          <span class="info-value">{{ emptySeatsCount }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Bots to Add:</span>
          <span class="info-value">{{ emptySeatsCount }}</span>
        </div>
      </div>

      <!-- Bot Difficulty Selection -->
      <div class="difficulty-section">
        <h4 class="section-title">
          <span class="material-symbols-outlined">psychology</span>
          <span>Bot Difficulty</span>
        </h4>
        <div class="difficulty-options">
          <button
            v-for="level in difficultyLevels"
            :key="level.value"
            @click="selectedDifficulty = level.value"
            class="difficulty-option"
            :class="{ selected: selectedDifficulty === level.value }"
          >
            <div class="option-icon" :class="level.value">
              <span class="material-symbols-outlined">{{ level.icon }}</span>
            </div>
            <div class="option-info">
              <span class="option-name">{{ level.name }}</span>
              <span class="option-desc">{{ level.description }}</span>
            </div>
          </button>
        </div>
      </div>

      <!-- Bot Benefits -->
      <div class="benefits-section">
        <h4 class="benefits-title">Bot Benefits:</h4>
        <ul class="benefits-list">
          <li>
            <span class="material-symbols-outlined">speed</span>
            <span>Start playing immediately without waiting for other players</span>
          </li>
          <li>
            <span class="material-symbols-outlined">school</span>
            <span>Practice game strategy and learn the rules</span>
          </li>
          <li>
            <span class="material-symbols-outlined">groups</span>
            <span>Bots can be replaced by real players who join later</span>
          </li>
        </ul>
      </div>

      <!-- Action Buttons -->
      <div class="fill-bots-actions">
        <button @click="handleCancel" class="btn-cancel">
          <span class="material-symbols-outlined">close</span>
          <span>Cancel</span>
        </button>
        <button @click="handleConfirm" class="btn-confirm">
          <span class="material-symbols-outlined">smart_toy</span>
          <span>Fill with Bots</span>
        </button>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import BaseModal from './BaseModal.vue'

/**
 * FillBotsModal Component
 *
 * Confirms filling empty seats with bot players.
 * Allows selection of bot difficulty level.
 *
 * Props:
 * - empty_seats: Array of empty seat indices
 * - default_difficulty: Default bot difficulty ('easy', 'medium', 'hard')
 *
 * Emits:
 * - confirm: { difficulty: string } - Fill with bots confirmed
 * - cancel: [] - Cancelled
 */

type BotDifficulty = 'easy' | 'medium' | 'hard'

interface Props {
  isOpen: boolean
  data: {
    empty_seats?: number[]
    default_difficulty?: BotDifficulty
  } | null
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  data: null,
})

const emit = defineEmits<{
  confirm: [{ difficulty: BotDifficulty }]
  cancel: []
  close: []
}>()

const selectedDifficulty = ref<BotDifficulty>(props.data?.default_difficulty || 'medium')

/**
 * Empty seats count
 */
const emptySeatsCount = computed(() => {
  return props.data?.empty_seats?.length || 0
})

/**
 * Difficulty levels
 */
const difficultyLevels = [
  {
    value: 'easy' as BotDifficulty,
    name: 'Easy',
    icon: 'sentiment_satisfied',
    description: 'Good for beginners and learning',
  },
  {
    value: 'medium' as BotDifficulty,
    name: 'Medium',
    icon: 'psychology',
    description: 'Balanced gameplay for practice',
  },
  {
    value: 'hard' as BotDifficulty,
    name: 'Hard',
    icon: 'trophy',
    description: 'Challenging for experienced players',
  },
]

/**
 * Handle confirm
 */
function handleConfirm(): void {
  emit('confirm', { difficulty: selectedDifficulty.value })
  emit('close')
}

/**
 * Handle cancel
 */
function handleCancel(): void {
  emit('cancel')
  emit('close')
}
</script>

<style scoped>
.fill-bots-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 0.5rem;
}

/* Info Icon */
.info-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(37, 99, 235, 0.2));
  display: flex;
  align-items: center;
  justify-content: center;
}

.info-icon .material-symbols-outlined {
  font-size: 3rem;
  color: #60a5fa;
}

/* Main Message */
.main-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  text-align: center;
}

.message-title {
  font-size: 1.375rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
}

.message-text {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  margin: 0;
  max-width: 450px;
}

/* Seats Info */
.seats-info {
  width: 100%;
  display: flex;
  gap: 1rem;
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
}

.info-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
}

.info-label {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.info-value {
  font-size: 2rem;
  font-weight: 700;
  color: #60a5fa;
}

/* Difficulty Section */
.difficulty-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.section-title .material-symbols-outlined {
  font-size: 1.25rem;
  color: #e8c547;
}

.difficulty-options {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.difficulty-option {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.difficulty-option:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.difficulty-option.selected {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(37, 99, 235, 0.15));
  border-color: #60a5fa;
  box-shadow: 0 0 20px rgba(59, 130, 246, 0.2);
}

.option-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.option-icon.easy {
  background: rgba(16, 185, 129, 0.2);
}

.option-icon.medium {
  background: rgba(251, 191, 36, 0.2);
}

.option-icon.hard {
  background: rgba(239, 68, 68, 0.2);
}

.option-icon .material-symbols-outlined {
  font-size: 1.75rem;
}

.option-icon.easy .material-symbols-outlined {
  color: #10b981;
}

.option-icon.medium .material-symbols-outlined {
  color: #fbbf24;
}

.option-icon.hard .material-symbols-outlined {
  color: #f87171;
}

.option-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.option-name {
  font-size: 1.125rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
}

.option-desc {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
}

/* Benefits Section */
.benefits-section {
  width: 100%;
  padding: 1.25rem;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 12px;
}

.benefits-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: rgba(147, 197, 253, 0.9);
  margin: 0 0 1rem;
}

.benefits-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}

.benefits-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.6;
}

.benefits-list li .material-symbols-outlined {
  font-size: 1.125rem;
  color: #60a5fa;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

/* Action Buttons */
.fill-bots-actions {
  display: flex;
  gap: 1rem;
  width: 100%;
  margin-top: 0.5rem;
}

.btn-cancel,
.btn-confirm {
  flex: 1;
  padding: 0.875rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: none;
}

.btn-cancel {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.btn-cancel:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.btn-confirm {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-confirm:hover {
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
  transform: translateY(-2px);
}

.btn-confirm:active,
.btn-cancel:active {
  transform: translateY(0);
}

.btn-cancel .material-symbols-outlined,
.btn-confirm .material-symbols-outlined {
  font-size: 1.25rem;
}

/* Responsive */
@media (max-width: 640px) {
  .info-icon {
    width: 64px;
    height: 64px;
  }

  .info-icon .material-symbols-outlined {
    font-size: 2.5rem;
  }

  .message-title {
    font-size: 1.125rem;
  }

  .message-text {
    font-size: 0.9375rem;
  }

  .seats-info {
    flex-direction: column;
    align-items: stretch;
  }

  .fill-bots-actions {
    flex-direction: column;
  }
}
</style>
